import {
  FEATURE_ANNOUNCEMENTS_ENABLED,
  FEATURE_SLIDES,
  type FeatureAnnouncementFlag,
  type FeatureSlideData
} from "@gettactic/defines/src/feature-announcements";
import { useMemo } from "react";

import { useSeenFeatures } from "./useSeenFeatures";

export interface UseUnseenFeaturesOptions {
  /** Map of feature flags to their enabled state */
  enabledFlags?: Partial<Record<FeatureAnnouncementFlag, boolean>>;
  /** Whether feature flags have finished loading. If false, will wait before determining features. */
  areFlagsLoaded?: boolean;
  /** Whether the current user is an admin. Used to filter admin-only announcements. */
  isAdmin?: boolean;
}

export const useUnseenFeatures = (options: UseUnseenFeaturesOptions = {}) => {
  const { enabledFlags = {}, areFlagsLoaded = true, isAdmin = false } = options;
  const { seenFeatures, isLoaded, markFeatureAsSeen, isFeatureSeen } =
    useSeenFeatures();

  // Consider fully loaded only when both localStorage and feature flags are ready
  const isFullyLoaded = isLoaded && areFlagsLoaded;

  // Filter features based on enabled flags, admin status, and sort by most recent
  const availableFeatures = useMemo((): FeatureSlideData[] => {
    if (!FEATURE_ANNOUNCEMENTS_ENABLED) return [];
    return FEATURE_SLIDES.filter((feature) => {
      // Filter out admin-only features for non-admins
      if (feature.adminOnly && !isAdmin) {
        return false;
      }
      // If no required flag, always available
      if (!feature.requiredFeatureFlag) {
        return true;
      }
      // Check if the required flag is enabled
      return enabledFlags[feature.requiredFeatureFlag] === true;
    }).sort(
      (a, b) =>
        new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
    );
  }, [enabledFlags, isAdmin]);

  const unseenFeatures = useMemo((): FeatureSlideData[] => {
    if (!isFullyLoaded) {
      return [];
    }

    const seenIds = new Set(seenFeatures.map((f) => f.id));

    // availableFeatures is already sorted by most recent, filter maintains order
    return availableFeatures.filter((feature) => !seenIds.has(feature.id));
  }, [seenFeatures, isFullyLoaded, availableFeatures]);

  const hasUnseenFeatures = useMemo(() => {
    // Don't show notification until fully loaded (localStorage + feature flags)
    if (!isFullyLoaded) {
      return false;
    }
    return unseenFeatures.length > 0;
  }, [unseenFeatures, isFullyLoaded]);

  const nextUnseenFeature = useMemo((): FeatureSlideData | null => {
    return unseenFeatures[0] ?? null;
  }, [unseenFeatures]);

  const unseenCount = useMemo(() => {
    return unseenFeatures.length;
  }, [unseenFeatures]);

  return {
    availableFeatures,
    unseenFeatures,
    hasUnseenFeatures,
    nextUnseenFeature,
    unseenCount,
    isLoaded: isFullyLoaded,
    markFeatureAsSeen,
    isFeatureSeen
  };
};
